<template>
  <VDropdown
    :placement="placement"
    :aria-id="ariaId"
    @apply-show="isFocusLoopActive = true"
    @apply-hide="isFocusLoopActive = false"
  >
    <button :class="triggerClass" test_id="navbar">
      <slot name="trigger" :is-live="instantSessions.isLive">
        <LivePill />
        <!-- Green ring while "Free to talk now" is on. -->
        <span
          class="inline-flex rounded-full"
          :class="{
            'ring-success-500 ring-2 ring-offset-2': instantSessions.isLive,
          }"
        >
          <Avatar
            :src="currentUser.avatarUrl"
            :alt="`${currentUser.firstName} ${currentUser.lastName}`"
            disable-lazy-loading
          />
        </span>
        <ChevronDownIcon class="ml-2 size-4" />
      </slot>
    </button>
    <template #popper>
      <div
        class="max-w-[300px] overflow-x-hidden overflow-y-hidden rounded-lg shadow-md"
      >
        <FocusLoop
          :is-active="isFocusLoopActive"
          :auto-focus="false"
          is-arrows-navigation-active
        >
          <div class="p-2">
            <InertiaLink
              :href="currentUser.profileUrl"
              class="flex items-center justify-between rounded-lg p-3 hover:bg-neutral-50"
            >
              <div class="flex min-w-0 items-center">
                <Avatar
                  :src="currentUser.avatarUrl"
                  disable-lazy-loading
                  size="lg"
                  class="mr-3 shrink-0"
                />
                <div class="min-w-0">
                  <div class="truncate font-semibold">
                    {{ currentUser.firstName }} {{ currentUser.lastName }}
                  </div>
                  <div class="text-xs text-neutral-400">view profile</div>
                </div>
              </div>
              <ChevronRightIcon class="ml-2 size-4 shrink-0 text-neutral-400" />
            </InertiaLink>
            <FreeToTalkToggle />
            <HalloweenHatPicker />
            <ul class="flex flex-col">
              <li
                v-for="(item, index) in navigation[accessControl.role]"
                :key="index"
                class="w-full"
              >
                <component
                  :is="item.as"
                  v-bind="{ ...item.attrs, ...item.actions }"
                  :href="item.isDisabled ? 'javascript:void(0)' : item.href"
                  class="block w-full rounded-lg px-4 py-2 text-left text-sm text-neutral-700 transition-colors hover:bg-neutral-50 hover:text-current focus:bg-neutral-100 focus:outline-none"
                  :class="{
                    'cursor-not-allowed': item.isDisabled,
                  }"
                >
                  <div class="inline-flex items-center">
                    <component
                      :is="item.icon"
                      class="mr-3 size-4.5"
                      :class="item.iconClasses"
                    />
                    <span>{{ item.name }}</span>
                    <span
                      v-if="item.isNew"
                      class="bg-success-100 text-success-600 ml-2 rounded-md px-1 py-0.5 text-xs"
                    >
                      New
                    </span>
                  </div>
                  <span
                    v-if="item.description"
                    class="mt-1 flex text-xs"
                    :class="{ 'text-neutral-400': item.isDisabled }"
                  >
                    {{ item.description }}
                  </span>
                </component>
                <hr v-if="item.hasBottomSeparator" class="my-1" />
              </li>
            </ul>
          </div>
        </FocusLoop>
      </div>
    </template>
  </VDropdown>
</template>

<script setup>
import Avatar from '../avatar/Avatar.vue'
import ChevronRightIcon from '~icons/lucide/chevron-right'
import FocusLoop from '../FocusLoop.vue'
import ChevronDownIcon from '~icons/lucide/chevron-down'
import { useCurrentUser } from '@/shared/composables/useCurrentUser'
import { ref, useId } from 'vue'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem'
import HalloweenHatPicker from '@/shared/components/halloween/HalloweenHatPicker.vue'
import FreeToTalkToggle from '@/modules/instant-sessions/components/FreeToTalkToggle.vue'
import LivePill from '@/modules/instant-sessions/components/LivePill.vue'
import { useInstantSessionsStore } from '@/modules/instant-sessions/stores/instantSessions'

defineProps({
  navigation: {
    type: Object,
    required: true,
  },
  // The sidebar opens the same menu from its profile block, upwards.
  placement: {
    type: String,
    default: 'bottom-end',
  },
  triggerClass: {
    type: String,
    default: 'inline-flex h-full items-center gap-2 p-1',
  },
})

const { accessControl } = usePermissionSystem()
const { currentUser } = useCurrentUser()
const instantSessions = useInstantSessionsStore()

const ariaId = useId()
const isFocusLoopActive = ref(false)
</script>
